// ============================================================================
// ui/mobile-launch.js — "Get started" → the tool takes the whole screen (phones)
// ============================================================================
// Ryan, 2026-07-26, opening the mobile pass: "do we want to change the way the
// app works on mobile so that there's a get started button… as opposed to
// scrolling down the website and then having to stop in the right place to use
// the tool and then worrying about accidentally scrolling too far and constantly
// having to readjust up and down to center the tool especially since it's so
// tall?"
//
// Yes — and it is a better fix than the one the mobile pass was going to make.
// Squeezing the panel to fit an inline slot treats the symptom; the real problem
// is a tall INTERACTIVE tool living inside a SCROLLING marketing page, where page
// scroll and tool interaction fight for every touch.
//
// So on a phone the page shows a card, and the tool opens over the whole screen.
//
// ── THE THREE THINGS THAT MAKE OR BREAK IT ──────────────────────────────────
//
//  1. `100dvh`, NEVER `100vh`. On iOS `100vh` resolves to the viewport with the
//     address bar HIDDEN — the largest it ever gets — so a 100vh element is
//     genuinely taller than what you can see and its bottom is unreachable until
//     you scroll. That IS the "stop in the right place / constantly readjust"
//     Ryan described, and shipping 100vh here would rebuild the bug inside the
//     fix. `dvh` tracks the visible area as the bar shows and hides.
//     (The CSS keeps a `100vh` line first as the fallback for old browsers; the
//     `dvh` line after it wins wherever it is understood.)
//
//  2. SCROLL LOCK MUST RESTORE THE POSITION. `overflow:hidden` on <body> makes
//     iOS forget where the page was, so closing the tool dumps you at the top of
//     a long marketing page — which reads as the site losing your place.
//
//  3. A HISTORY ENTRY. Without one, Android's back button and iOS's swipe-back
//     LEAVE THE SITE from a full-screen tool, because nothing on screen looks
//     like a page you can go back within. Pushing state makes back mean "close
//     the tool", which is what the gesture is asking for.
//
// The tool also does not BOOT until the button is tapped, which is the point
// rather than a bonus: the 3D chunk, the font library and the texture pipeline
// stop loading for every phone visitor who scrolls past.
// ============================================================================

import { h } from './dom.js';
import { media } from './dom.js';

/* ── THE CARD'S ART (2026-08-13) ─────────────────────────────────────────────────────────────
   A pre-rendered plate turning through a full revolution, looping. It is the third thing this card
   has shown and the reasons the first two failed are worth keeping:

     1. A bumper plate drawn from radial + conic gradients. Ryan: *"this weird symbol above the
        button."* It was approximating a photograph we did not have.
     2. Four real product photographs, rotating. Better, and still wrong for two reasons Ryan named
        at once: *"Easton image is way too overused"* — the same identity was already on the hero
        and in the category section directly above — and a still photo does not say the thing this
        card is actually selling, which is that the plate is LIVE and you can turn it.

   A spin says "3D tool" in a way no still can, and it says it before the tap.

   ⛔ PRE-RENDERED, NEVER THE LIVE RENDERER. The launch card exists so that nothing heavy loads
   until the tap — no Three.js, no texture pipeline, no font library. Spinning a real plate here
   would import the exact chunk the card defers, for every phone visitor who scrolls past. The frames
   come off the app's own renderer via `tools/gen-launch-spin.mjs` and ship as one finished image.

   ⚠ ANIMATED WEBP, FOR ALPHA. The card no longer draws a background (see below), so the plate sits
   directly on whatever the page puts behind it and the loop has to carry transparency. H.264 has
   none, and transparent WebM/HEVC is a per-browser minefield. */
import ART_SPIN from './launch/launch-spin.webp';

/** The plan's verbatim detection rule (§8 "Detection: `(max-width:768px) && (pointer:coarse)`, no
 *  exceptions"). The width clause is load-bearing: Ryan's own laptop is a touchscreen and reports a
 *  COARSE pointer, so a pointer-only test would put the desktop into the phone's flow. */
export function isPhone() {
  try {
    return media('(max-width: 768px)').matches && media('(pointer: coarse)').matches;
  } catch {
    return false;
  }
}

const ICON_CLOSE =
  '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';

/**
 * Put the launch card in the mount and resolve when the buyer opens the tool.
 * Resolves immediately (without a card) on anything that isn't a phone.
 *
 * @param {HTMLElement} target the app's mount element
 * @param {{autoOpen?: boolean}} [opts] `autoOpen` opens the tool without waiting for the tap — see
 *        the note at the bottom of this function. Used for a shared `?d=` design.
 * @returns {Promise<void>} resolves once the tool should mount, full-screen
 */
let cardEl = null;
let cardBtn = null;
let booted = false;   // has the app mounted behind the card?

export function awaitMobileLaunch(target, { autoOpen = false } = {}) {
  if (!isPhone()) return Promise.resolve();

  return new Promise((resolve) => {
    /* One label, in one state, for every visitor: what is behind this card is the gate, and the
       gate is where the choices are.

       ⛔ "Design my weights" → "Design Yours" (Ryan 2026-08-15). Not a preference — the old label
       was the SAME WORDS as the who-card's CTA (`ui/logo-step.js`, `.wd-wtf-go`), three taps later,
       on a button that does something completely different: this one opens a picker, that one
       builds the design. A phone buyer read the sequence
           hero "Start Designing" → THIS → gate "Next" → gate "Start designing"
           → who-card "Design My Weights" → tab "Customize Design"
       and met the same promise twice while the two obvious replacements collided too — "Start
       Design" with the hero above it AND the gate one tap below, "Customize Weights" with the
       Customize Design tab and the gate's own "Pick some weights to customize."
         "Design Yours" is the one phrase in that space nothing else uses, and it does not overclaim:
       the tap opens a tool, it does not design anything yet. It is also the label this card carried
       before 2026-08-09, so it is already in Ryan's voice.
       ⚠ IF YOU RENAME IT AGAIN, read the six lines above first — the flow is crowded and every
       nearby label is already spoken for. */
    cardBtn = h('button', { type: 'button', class: 'wd-launch__btn' }, 'Design Yours');

    /* ⛔ ONE LABEL, WHATEVER THE STATE (Ryan 2026-08-09). This same button is also the RESUME
       control after the tool has been closed once, and it deliberately does NOT relabel itself —
       the gate one tap later carries a Resume pill of its own and says it properly. See `park`. */

    /* ⛔ FOUR THINGS, AND NOTHING ELSE (Ryan 2026-08-14). The card carries a headline, the four
       steps, the turning plate and the button. Everything else has now been cut TWICE, from both
       directions, and it is worth recording what went and why so it does not creep back a third
       time:
         · a lead sales line ("Put your logo and colors on real bumper plates") — cut 2026-08-09 for
           re-selling what the page hero directly above already said, bigger;
         · a time-cost subhead ("Design it yourself in about two minutes"), a mechanics line ("Free
           to use, no account needed · Opens full screen") and a proof promise ("Nothing is made
           until you approve a proof") — cut 2026-08-14.
       The last three were each defensible on their own and collectively turned a launch card into a
       landing page. The steps already say what the tool does and the plate already says it is real;
       reassurance belongs on the screens where it is actually needed, and the proof promise still
       runs on the saved-design modal (`ui/share.js`) where it lands at the moment it matters. */
    cardEl = h(
      'div',
      { class: 'wd-launch' },
      h(
        'div',
        { class: 'wd-launch__body' },
        h('p', { class: 'wd-launch__title' }, 'Design your next weight set today'),
        /* The four steps come from the trust banner that now hides on mobile — the shape of the
           task is worth more inside the card a buyer is deciding whether to tap than in a band
           above it. Separators are their own spans so they can be dimmed without dimming the
           steps (2026-08-13). */
        h(
          'p',
          { class: 'wd-launch__steps' },
          h('span', { class: 'wd-launch__step' }, 'Pick weights'),
          h('span', { class: 'wd-launch__sep', 'aria-hidden': 'true' }, '›'),
          h('span', { class: 'wd-launch__step' }, 'Add logo'),
          h('span', { class: 'wd-launch__sep', 'aria-hidden': 'true' }, '›'),
          h('span', { class: 'wd-launch__step' }, 'Customize'),
          h('span', { class: 'wd-launch__sep', 'aria-hidden': 'true' }, '›'),
          h('span', { class: 'wd-launch__step' }, 'Get quote'),
        ),
        /* BELOW the text and directly ABOVE the button (Ryan: *"a spinning plate or something below
           the text"*). The visual proof sits in the last position before the action, and the button
           still ends the card — a CTA that has something under it stops reading as the CTA. */
        h('img', {
          class: 'wd-launch__spin',
          src: ART_SPIN,
          alt: 'A custom branded bumper plate turning',
          /* Eager: it is the only thing here that can be slow and it is on screen immediately.
             `width`/`height` are stated so the card reserves the box before the bytes land — a
             layout that shifts under a thumb mid-read is most of what "rushed" looked like. */
          loading: 'eager',
          fetchpriority: 'high',
          decoding: 'async',
          width: '260',
          height: '260',
        }),
        cardBtn,
      ),
    );
    target.replaceChildren(cardEl);

    // NOT `{ once: true }`: this same button is the RESUME control after the tool has been closed
    // once (Ryan 2026-07-26 — closing used to drop you back onto the live inline embed, which is
    // the exact tall-tool-in-a-scrolling-page problem the full-screen mode exists to remove).
    /* The first open, from whichever door — the tap or `autoOpen` below. One function because the
       two must do the SAME three things: mark the boot, take the screen (scroll lock + history
       entry + 100dvh), and let `mount` proceed. */
    const launch = () => {
      booted = true;
      openFullScreen(target);
      // The card is replaced by the app itself once `mount` runs; removing it here would leave a
      // blank screen for the length of the boot, which on a phone is exactly when someone decides
      // the thing is broken.
      resolve();
    };

    // NOT `{ once: true }`: this same button is the RESUME control after the tool has been closed
    // once (Ryan 2026-07-26 — closing used to drop you back onto the live inline embed, which is
    // the exact tall-tool-in-a-scrolling-page problem the full-screen mode exists to remove).
    cardBtn.addEventListener('click', () => {
      if (booted) {
        // Already mounted and parked: un-park and re-open. Deliberately NOT a re-mount — the design
        // is in the mounted app, and rebooting would throw it away and pay for the 3D chunk twice.
        if (cardEl.parentElement) cardEl.remove();
        target.style.display = '';
        openFullScreen(target);
        return;
      }
      launch();
    });

    /* ⛔ A SHARED DESIGN OPENS ITSELF (PRESENTATION-MODE-PLAN.md, Phase 1).
         This card exists so that a phone visitor SCROLLING PAST a marketing page does not pay for
       the 3D chunk, the font library and the texture pipeline — that is the whole reason the tool
       does not boot until the tap. Someone who followed a link to one specific design is the exact
       opposite of that visitor: they were sent a plate, the page they landed on has nothing else on
       it for them, and "Design Yours" is an invitation to start something when they came to look at
       something that already exists.
         Left in, it also breaks the feature outright on its PRIMARY device. The reader is an athletic
       director checking mail between practices, on a phone — so the one surface presentation mode is
       designed for is the one where a share link showed a generic card instead of their school's
       logo.
       ⚠ AUTO-OPENED, NOT SKIPPED. It goes through `launch()` like a tap would, because full-screen
       mode is not decoration: it is the scroll lock, the history entry that makes Android's back
       button mean "close the tool", and the `100dvh` sizing. Bypassing the card and mounting inline
       would hand a phone the tall-tool-in-a-scrolling-page problem this module was built to end.
       None of it needs a user gesture — this is CSS and history, not the Fullscreen API. */
    if (autoOpen) launch();
  });
}

/**
 * PARK the tool behind its card instead of leaving it live inline.
 *
 * Closing used to hand the page back its embedded designer — a tall interactive tool inside a
 * scrolling marketing page, which is precisely what full-screen mode exists to avoid. So the mount
 * is hidden (not destroyed: the buyer's design lives in it) and the card returns in its place,
 * unchanged — see the note inside for why it stopped relabelling itself.
 */
function park(target) {
  if (!cardEl || !target.parentElement) return;
  /* ⛔ THE CARD NO LONGER RELABELS ITSELF (Ryan 2026-08-09: *"sometimes when I refresh it'll have a
     pill on the main screen for 'Resume yours'... this is no longer needed. Should just be the
     'Design yours' pill, b/c when you click on that, the next screen presents the next or resume
     design buttons"*).
       It used to read "Resume designing" over "Your design is saved" once the tool had been closed.
     That was the right answer when the only other place to say it was a modal at first paint — and
     it is the wrong one now that the gate carries a Resume pill of its own: the card would be
     answering a question the very next screen asks properly, one screen early and in different
     words. One label, one state, whatever is or is not saved.
       ⚠ NOTHING ABOUT THE WORK CHANGED. The mount is still HIDDEN rather than destroyed, so tapping
     the card in the same session re-opens the live app with the design exactly where it was — which
     is why the card never needed to promise it. */
  target.style.display = 'none';
  target.parentElement.insertBefore(cardEl, target);
}

/** Scroll position captured at open, so closing can put the page back where it was. */
let savedScrollY = 0;
let closeHandler = null;
/** Everything we hid to isolate the tool, so close can put it all back. */
let hidden = [];
/** Where the mount lived before we moved it, so close can put it back exactly. */
let homeParent = null;
let homeNext = null;

/**
 * PORTAL the mount to <body>.
 *
 * Hiding the siblings (below) stopped the site's footer painting over the tool, and revealed the
 * other half of the problem: a big white rectangle covering most of the screen (Ryan 2026-07-26).
 * That was the tool's own ANCESTOR showing, because the overlay was never covering the viewport in
 * the first place.
 *
 * `position: fixed` is only relative to the viewport while NO ancestor has a transform, filter,
 * perspective, backdrop-filter or will-change. Any one of those makes the ancestor the containing
 * block, so `inset: 0` resolves to that element's box — the overlay shrinks to its container and
 * the container's own background fills the rest of the screen. Themes apply transforms freely
 * (scroll reveals, carousels, sticky wrappers), so the designer cannot assume its embed slot is
 * clean; it has to leave.
 *
 * Safe to move: this runs BEFORE `mount()`, so the element is still empty — no canvas, no WebGL
 * context, nothing to reparent but a bare div.
 */
function portalOut(target) {
  homeParent = target.parentElement;
  homeNext = target.nextElementSibling;
  document.body.appendChild(target);
}

function portalHome(target) {
  if (!homeParent) return;
  // A resize event after the move: the stage measures its own box, and reparenting changes it.
  if (homeNext && homeNext.parentElement === homeParent) homeParent.insertBefore(target, homeNext);
  else homeParent.appendChild(target);
  homeParent = null;
  homeNext = null;
  try { window.dispatchEvent(new Event('resize')); } catch { /* non-fatal */ }
}

/**
 * Hide every SIBLING on the path from the mount up to <body>, so nothing of the site can appear
 * over or around the full-screen tool (Ryan 2026-07-26: "the footer from the site comes up really
 * high and overlaps half the app").
 *
 * TWO causes, and only one of them is z-index — which is why raising the number wasn't the fix:
 *
 *  1. THE LOCK REFLOWS THE PAGE. `body { position: fixed }` is the only scroll lock iOS honours,
 *     and it takes the body out of normal flow, COLLAPSING the document's height. A footer that
 *     sat 3000px down is suddenly near the top of the viewport. It didn't rise over the tool; the
 *     page shrank underneath it.
 *  2. STACKING CONTEXT, NOT Z-INDEX. The overlay is `position: fixed` with a near-max z-index, but
 *     any ancestor with a transform / filter / will-change seals that number INSIDE its own
 *     context, and a sticky footer outside that context then wins whatever the number says.
 *
 * Hiding the siblings answers both at once, and it cannot be defeated by a theme change: the tool's
 * own ancestor chain stays visible (it has to — it contains the tool), and everything else on the
 * page is `display: none` for the duration. Reversible, and it touches no theme CSS.
 */
function isolate(target) {
  hidden = [];
  let node = target;
  while (node && node.parentElement && node.parentElement !== document.documentElement) {
    for (const sib of Array.from(node.parentElement.children)) {
      if (sib === node || sib.classList.contains('wd-fs-close')) continue;
      if (sib.hasAttribute('data-wd-was-hidden')) continue;
      hidden.push([sib, sib.style.display]);
      sib.setAttribute('data-wd-was-hidden', '1');
      sib.style.display = 'none';
    }
    node = node.parentElement;
  }
}

function deIsolate() {
  for (const [el, display] of hidden) {
    el.style.display = display || '';
    el.removeAttribute('data-wd-was-hidden');
  }
  hidden = [];
}

// ── THE KEYBOARD MUST SHRINK THE PAGE, NOT SLIDE OVER IT (Ryan 2026-07-28) ───────────────────────
// Two symptoms, one cause: "the bottom of the text tab includes a big empty blank strip… but only
// when the keyboard appears", and "the top tabs and hamburger disappear when you go to type".
//
// Chrome's default is `interactive-widget=resizes-visual`: the on-screen keyboard leaves the LAYOUT
// viewport at full height and merely slides the VISUAL one up over it. So `height: 100dvh` on the
// full-screen root still measures the whole screen — the app goes on being laid out behind the
// keyboard (that dead strip below the last field is the part of it the keyboard is covering), and to
// get the focused input into view Chrome scrolls the visual viewport, which carries the top tabs and
// the hamburger off the top edge. Nothing was hidden; the page was moved out from under them.
//
// `resizes-content` makes the keyboard shrink the layout viewport instead. `100dvh` then resolves to
// the space actually left, the app re-lays out into it, and nothing needs scrolling anywhere — the
// top chrome stays where it is because it never moved.
//
// Patched at RUNTIME rather than in the markup because on the storefront the tag belongs to the
// Shopify theme, and this is a page-wide policy the designer has no business owning outside its own
// full-screen mode. Restored verbatim on exit.
let savedViewportContent = null;
function setKeyboardResizesContent(on) {
  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta) return;
  if (on) {
    if (savedViewportContent === null) savedViewportContent = meta.getAttribute('content') || '';
    if (/interactive-widget=/.test(savedViewportContent)) return; // the theme has an opinion — leave it
    meta.setAttribute('content', `${savedViewportContent}, interactive-widget=resizes-content`);
  } else if (savedViewportContent !== null) {
    meta.setAttribute('content', savedViewportContent);
    savedViewportContent = null;
  }
}

/* ── DESKTOP: FILL THE BROWSER WINDOW, NOT THE MONITOR (Ryan 2026-08-16) ──────────────────────────
 * *"Wonder if there's a way to make it full screen.. but full screen at the width of your current
 * internet window (if you have one open). as opposed to full screen across your entire monitor which
 * is what the current full screen button does on desktop."*
 *
 * There is, and it is the mechanism this module already uses on phones: `.wd-fs` is `position: fixed;
 * inset: 0` plus a scroll lock — **CSS, not the Fullscreen API**. That is precisely the difference he
 * is describing: the Fullscreen API asks the OS for the monitor; this asks for the viewport. The only
 * reason a desktop visitor never got it is that the overlay's rules live inside the phone media query.
 *
 * ⛔ DELIBERATELY LIGHTER THAN `openFullScreen`. No `portalOut`, no hamburger menu, no history entry —
 * because this lock exists ONLY while a shared design is being presented, and that view already has
 * exactly one way out on screen. "Edit this design" releases the lock and opens the editor in the same
 * gesture, so there is never a locked screen without a visible exit. Adding a second exit control
 * would be a second thing to find, for a state that lasts until the first click.
 */
let viewportLockScrollY = 0;
let viewportLocked = false;

export function openViewportLock(target) {
  if (viewportLocked || !target) return;
  viewportLocked = true;
  viewportLockScrollY = window.scrollY || window.pageYOffset || 0;
  document.documentElement.classList.add('wd-fs-open');
  document.body.classList.add('wd-fs-open');
  document.body.style.top = `-${viewportLockScrollY}px`;
  target.classList.add('wd-fs', 'wd-fs-viewport');
}

export function closeViewportLock(target) {
  if (!viewportLocked) return;
  viewportLocked = false;
  if (target) target.classList.remove('wd-fs', 'wd-fs-viewport');
  document.documentElement.classList.remove('wd-fs-open');
  document.body.classList.remove('wd-fs-open');
  document.body.style.top = '';
  /* Put the page back exactly where it was, then leave them looking AT THE TOOL rather than at the
     hero they never asked for — the same thing the arrival scroll does, for the same reason. */
  window.scrollTo(0, viewportLockScrollY);
  try { target?.scrollIntoView({ block: 'start', inline: 'nearest' }); } catch { /* older browsers */ }
}

function openFullScreen(target) {
  savedScrollY = window.scrollY || window.pageYOffset || 0;
  setKeyboardResizesContent(true);
  document.documentElement.classList.add('wd-fs-open');
  document.body.classList.add('wd-fs-open');
  // Hold the page in place visually while it is locked. `position: fixed` on the body is the only
  // approach iOS honours; `overflow: hidden` alone still lets the page move under the overlay.
  document.body.style.top = `-${savedScrollY}px`;
  target.classList.add('wd-fs');
  portalOut(target);   // leave the embed slot BEFORE hiding anything — see portalOut
  isolate(target);

  // Back / swipe-back closes the TOOL, not the site.
  try {
    history.pushState({ wdFullScreen: true }, '');
    /* ⛔ ONLY WHEN THE ENTRY BEING POPPED IS OURS. This used to close on ANY `popstate`, which was
       correct while the tool owned the only entry — and became a bug the moment something else
       inside it pushed one too. The chat panel now does (`ui/help-assist.js`), so with it open the
       stack is [tool][chat] and a single back fired BOTH handlers: the chat closed and the app went
       with it (Ryan 2026-08-09, Android).
         `history.state` after the pop tells us where we LANDED. Landing back on our own
       `wdFullScreen` entry means an inner overlay was dismissed and the tool is still the thing on
       screen — so it stays. Only a pop that leaves our entry behind closes it.
         ⚠ Deliberately a STATE test rather than listener ordering or `stopImmediatePropagation`:
       both of those depend on which module happened to register first, which is a boot-order detail
       nobody should have to keep true. */
    closeHandler = () => {
      if (history.state && history.state.wdFullScreen) return;
      closeFullScreen(target, { fromHistory: true });
    };
    window.addEventListener('popstate', closeHandler);
  } catch { /* history blocked — the ✕ still works */ }

  // ON THE BODY, not inside the mount — and that is not tidiness, it is the only place it survives.
  // `mount()` calls `target.replaceChildren()` to clear the Liquid fallback once it owns the
  // element, which silently ate a close button appended here first: the tool opened full screen
  // with no way out but the browser's back gesture. The button is `position: fixed` regardless, so
  // it has no reason to live inside the mount.
  if (!document.querySelector('.wd-fs-close')) buildFullScreenMenu(target);
}

// ── THE CORNER CONTROL IS A MENU, NOT AN EXIT (Ryan 2026-07-27) ──────────────────────────────
// "instead of the 'x' in the top right corner to close the app on mobile, I think it should be a
// hamburger… which when clicked, brings up a menu, which has in it a close button. That way it's
// harder for folks to accidentally click the 'x' and close the app when they were really just
// trying to click the zoom button or something."
//
// The ✕ sat one thumb-width from the zoom control and did something unrecoverable — you lose the
// screen you were working on. A hamburger costs a deliberate second tap before anything is thrown
// away, which is the right price for the only irreversible control in the tool.
//
// EVERY NUMBER HERE IS THE STOREFRONT'S. The theme's own toggle (`.main-menu__toggle`, main.css) is
// a 44×44 box holding FOUR 20×2px bars: two at y14/y28, two stacked at y21. Opening collapses the
// outer pair to zero width at the centre and rotates the middle pair ±45° into an X, all on
// `0.3s ease-in-out` over `transform, width, top, left`. The panel is a plain 0.3s opacity +
// visibility fade. Copied rather than approximated, because "feels like a Blue Sky product" is a
// claim about muscle memory: a buyer who has used the site's menu should find this one behaves
// identically, and a hand-tuned near-miss is exactly what would give it away.
// Items the APP contributes — Save/Share moved in here (Ryan 2026-07-27: "less is more on the main
// screen, and the hamburger symbol will garner plenty of clicks from people who understand it as
// the universal location to find those sorts of settings/options"). Held at module scope because
// the menu is built the moment the tool goes full screen, which is BEFORE main.js has booted and
// knows what those actions are; whoever arrives second fills in the other's blank.
let appMenuItems = [];
let renderMenu = null;
/** Fired when the full-screen menu OPENS — see `setFullScreenMenuOnOpen`. */
let appMenuOnOpen = null;

/**
 * Register a callback for the moment the hamburger menu opens.
 *
 * Added for "Share via…" (Ryan 2026-07-30) and this is the surface it matters most on, since the OS
 * share sheet is a phone feature first. `navigator.share()` needs transient user activation, so the
 * share link must already exist when the row is tapped — awaiting it first and then calling share()
 * gets `NotAllowedError` on iOS Safari. Opening the menu is the last moment before the tap, so it is
 * where the link gets warmed.
 *   Same contract as the canvas cluster's `onShareOpen`: cheap and idempotent, because a buyer may
 * open and close this menu repeatedly.
 */
export function setFullScreenMenuOnOpen(fn) {
  appMenuOnOpen = typeof fn === 'function' ? fn : null;
}

/**
 * Hand the full-screen menu the app's own actions. Safe to call before or after the menu exists.
 * @param {{icon?:string,label:string,onClick:(e:Event,btn:HTMLElement)=>void}[]} items
 */
export function setFullScreenMenuItems(items) {
  appMenuItems = Array.isArray(items) ? items : [];
  if (renderMenu) renderMenu();
}

function buildFullScreenMenu(target) {
  const btn = h('button', {
    type: 'button', class: 'wd-fs-close wd-fs-burger',
    'aria-label': 'Menu', 'aria-expanded': 'false', 'aria-haspopup': 'true',
  }, h('span', { class: 'wd-fs-burger__icon', 'aria-hidden': 'true' }));

  const card = h('div', { class: 'wd-fs-menu__card' });
  const sheet = h('div', { class: 'wd-fs-menu', role: 'menu', 'aria-label': 'Designer menu' }, card);

  let open = false;
  const row = (icon, label, onClick, extraClass) => {
    const b = h('button', { type: 'button', class: `wd-fs-menu__item${extraClass ? ` ${extraClass}` : ''}`, role: 'menuitem' },
      icon ? h('span', { class: 'wd-fs-menu__icon', 'aria-hidden': 'true', html: icon }) : null,
      h('span', {}, label));
    b.addEventListener('click', (e) => onClick(e, b));
    return b;
  };
  // CLOSE IS LAST, under a divider, and it is the only destructive row in here. Putting it at the
  // top next to Copy link is how a buyer loses their design reaching for a share sheet — the same
  // near-miss that moved this whole control off the canvas in the first place.
  renderMenu = () => {
    card.replaceChildren(
      ...appMenuItems.map((it) => row(it.icon, it.label, (e, b) => { setOpen(false); it.onClick(e, b); })),
      appMenuItems.length ? h('div', { class: 'wd-fs-menu__sep', 'aria-hidden': 'true' }) : null,
      row(ICON_CLOSE, 'Close designer', () => { setOpen(false); closeFullScreen(target); }, 'wd-fs-menu__item--danger'),
    );
  };
  const setOpen = (on) => {
    open = on;
    // Give the rows a head start on anything they need ready for a TAP (the share link) — see
    // `setFullScreenMenuOnOpen`. Guarded so a warm-up failure can never stop the menu opening.
    if (on && appMenuOnOpen) { try { appMenuOnOpen(); } catch { /* never block the menu */ } }
    btn.classList.toggle('is-open', on);
    sheet.classList.toggle('is-open', on);
    btn.setAttribute('aria-expanded', on ? 'true' : 'false');
    btn.setAttribute('aria-label', on ? 'Close menu' : 'Menu');
  };
  btn.addEventListener('click', (e) => { e.stopPropagation(); setOpen(!open); });
  renderMenu();
  // Tap anywhere off the card, or Escape — the same two dismissals every other overlay in the tool
  // honours. Nothing here closes the DESIGNER; dismissing a menu must never be destructive.
  sheet.addEventListener('click', (e) => { if (e.target === sheet) setOpen(false); });
  const onKey = (e) => { if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); } };
  document.addEventListener('keydown', onKey);
  sheet._wdOnKey = onKey;

  document.body.append(btn, sheet);
}

/**
 * Is the tool currently taking the whole screen?
 *
 * Asked by the help panel's "More in the Help & Guide" deep links, which scroll the page underneath
 * — and a page scrolling underneath a full-screen overlay is a link that does nothing. Reads the
 * class rather than a flag of its own: `wd-fs-open` is what every other consumer already keys off,
 * and a second source of truth for "is it open" is a state to keep in step for no gain.
 */
export function isFullScreenOpen() {
  try { return document.documentElement.classList.contains('wd-fs-open'); } catch { return false; }
}

function closeFullScreen(target, { fromHistory = false } = {}) {
  setKeyboardResizesContent(false);
  document.documentElement.classList.remove('wd-fs-open');
  document.body.classList.remove('wd-fs-open');
  document.body.style.top = '';
  target.classList.remove('wd-fs');
  deIsolate();
  portalHome(target);
  window.scrollTo(0, savedScrollY);
  document.querySelectorAll('.wd-fs-close').forEach((n) => n.remove());
  document.querySelectorAll('.wd-fs-menu').forEach((n) => {
    if (n._wdOnKey) document.removeEventListener('keydown', n._wdOnKey);
    n.remove();
  });
  renderMenu = null;
  if (closeHandler) {
    window.removeEventListener('popstate', closeHandler);
    closeHandler = null;
  }
  // Only unwind the history entry when the CLOSE BUTTON was the trigger — a popstate has already
  // consumed it, and calling back() again would walk the buyer off the site.
  if (!fromHistory) {
    try { history.back(); } catch { /* nothing to unwind */ }
  }
  park(target);
}
